import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Upload, Shield, Search, AlertCircle, CheckCircle2, Copy } from "lucide-react";
import { tr } from "../contexts/LangContext";

const REG_TABS = [
  { key: "pending", label: "En attente" },
  { key: "unverified", label: "Non vérifiés" },
  { key: "verified", label: "Vérifiés / Actifs" },
  { key: "rejected", label: "Rejetés" },
  { key: "spam", label: "Spam" },
];

function RegistrationsPanel() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async (t = tab) => {
    try { const { data } = await api.get(`/admin/registrations?status=${t}`); setRows(data || []); setSel([]); }
    catch { setRows([]); }
  };
  useEffect(() => { load(tab); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const bulk = async (action) => {
    if (sel.length === 0) return;
    if (action === "delete" && !window.confirm(tr("Supprimer définitivement la sélection ?"))) return;
    setBusy(true);
    try { await api.post("/admin/registrations/bulk", { ids: sel, action }); await load(); }
    catch { /* ignore */ }
    setBusy(false);
  };

  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSel = rows.length > 0 && sel.length === rows.length;

  return (
    <div className="border border-slate-200 bg-white p-5 mb-8" data-testid="registrations-panel">
      <h3 className="font-serif text-xl text-navy mb-3">{tr("Inscriptions publiques")}</h3>
      <div className="flex flex-wrap gap-1 border-b border-slate-200 mb-4">
        {REG_TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-xs uppercase tracking-widest border-b-2 ${tab === t.key ? "border-club-red text-club-red font-semibold" : "border-transparent text-slate-500 hover:text-navy"}`}
            data-testid={`reg-tab-${t.key}`}>{tr(t.label)}</button>
        ))}
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-slate-400" data-testid="reg-empty">{tr("Aucune inscription dans cette catégorie.")}</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-3">
            <button disabled={busy || sel.length === 0} onClick={() => bulk("approve")} className="bg-green-700 text-white px-3 py-1.5 text-[11px] uppercase tracking-widest disabled:opacity-40" data-testid="reg-bulk-approve">{tr("Approuver la sélection")}</button>
            <button disabled={busy || sel.length === 0} onClick={() => bulk("reject")} className="bg-slate-600 text-white px-3 py-1.5 text-[11px] uppercase tracking-widest disabled:opacity-40" data-testid="reg-bulk-reject">{tr("Rejeter la sélection")}</button>
            <button disabled={busy || sel.length === 0} onClick={() => bulk("spam")} className="bg-amber-600 text-white px-3 py-1.5 text-[11px] uppercase tracking-widest disabled:opacity-40" data-testid="reg-bulk-spam">{tr("Marquer comme spam")}</button>
            <button disabled={busy || sel.length === 0} onClick={() => bulk("delete")} className="bg-red-700 text-white px-3 py-1.5 text-[11px] uppercase tracking-widest disabled:opacity-40" data-testid="reg-bulk-delete">{tr("Supprimer la sélection")}</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left text-xs uppercase tracking-widest text-slate-500">
                <th className="px-2 py-2"><input type="checkbox" checked={allSel} onChange={() => setSel(allSel ? [] : rows.map((r) => r.id))} data-testid="reg-select-all" /></th>
                <th className="px-2 py-2">{tr("Nom")}</th><th className="px-2">Email</th><th className="px-2">{tr("Statut")}</th><th className="px-2">{tr("Inscrit le")}</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100" data-testid={`reg-row-${r.id}`}>
                    <td className="px-2 py-2"><input type="checkbox" checked={sel.includes(r.id)} onChange={() => toggle(r.id)} data-testid={`reg-check-${r.id}`} /></td>
                    <td className="px-2">{r.name || "—"}</td>
                    <td className="px-2">{r.email}</td>
                    <td className="px-2 text-xs uppercase tracking-widest text-slate-500">{r.status}</td>
                    <td className="px-2 text-xs text-slate-400">{(r.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const ROLES = [
  { value: "member",    label: "Membre",          color: "bg-slate-100 text-slate-700" },
  { value: "treasurer", label: "Trésorier",       color: "bg-amber-100 text-amber-800" },
  { value: "secretary", label: "Secrétaire",      color: "bg-emerald-100 text-emerald-800" },
  { value: "manager",   label: "Gestion",         color: "bg-cyan-100 text-cyan-800" },
  { value: "president", label: "Président",       color: "bg-fuchsia-100 text-fuchsia-800" },
  { value: "admin",     label: "Administrateur",  color: "bg-red-100 text-red-800" },
];

export default function AdminUsersTab() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState("");
  const [status, setStatus] = useState({ ok: "", err: "" });
  const [showImport, setShowImport] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data || []);
    } catch (e) {
      setStatus({ err: e.response?.data?.detail || tr("Erreur de chargement"), ok: "" });
    }
  };
  useEffect(() => { load(); }, []);

  const changeRole = async (id, role) => {
    setBusyId(id); setStatus({ ok: "", err: "" });
    try {
      await api.patch(`/admin/users/${id}/role`, { role });
      setStatus({ ok: `Rôle mis à jour : ${role}`, err: "" });
      await load();
      setTimeout(() => setStatus({ ok: "", err: "" }), 3000);
    } catch (e) {
      setStatus({ err: e.response?.data?.detail || "Erreur", ok: "" });
    } finally {
      setBusyId("");
    }
  };

  const updateRoles = async (id, roles) => {
    setBusyId(id); setStatus({ ok: "", err: "" });
    try {
      const { data } = await api.patch(`/admin/users/${id}/roles`, { roles });
      setStatus({ ok: `Rôles mis à jour : ${data.roles.join(" · ")}`, err: "" });
      await load();
      setTimeout(() => setStatus({ ok: "", err: "" }), 3500);
    } catch (e) {
      setStatus({ err: e.response?.data?.detail || "Erreur", ok: "" });
    } finally {
      setBusyId("");
    }
  };

  const [editingUser, setEditingUser] = useState(null);
  const [detailsUser, setDetailsUser] = useState(null);
  const [actionBusyId, setActionBusyId] = useState("");

  const emailFiche = async (u) => {
    if (!u.email || u.email.endsWith("@rycm-imported.local")) {
      setStatus({ err: tr("Email manquant ou placeholder — éditez la fiche d'abord."), ok: "" });
      return;
    }
    if (!window.confirm(`Envoyer la fiche PDF par email à ${u.email} ?`)) return;
    setActionBusyId(u.id); setStatus({ ok: "", err: "" });
    try {
      const { data } = await api.post(`/admin/members/${u.id}/email-fiche`);
      setStatus({ ok: data.message || tr("Email envoyé"), err: "" });
    } catch (e) {
      setStatus({ err: e.response?.data?.detail || "Erreur d'envoi", ok: "" });
    } finally {
      setActionBusyId(""); setTimeout(() => setStatus({ ok: "", err: "" }), 4000);
    }
  };

  const revokeSessions = async (u) => {
    if (!window.confirm(`Forcer la déconnexion de ${u.email} ?\nIl/elle devra se reconnecter immédiatement.`)) return;
    setActionBusyId(u.id); setStatus({ ok: "", err: "" });
    try {
      await api.post(`/admin/users/${u.id}/sessions/revoke`);
      setStatus({ ok: tr("Sessions révoquées avec succès."), err: "" });
    } catch (e) {
      setStatus({ err: e.response?.data?.detail || "Erreur", ok: "" });
    } finally {
      setActionBusyId(""); setTimeout(() => setStatus({ ok: "", err: "" }), 4000);
    }
  };

  const filtered = users.filter((u) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [u.email, u.name, u.first_name, u.last_name, u.role]
      .some((v) => (v || "").toLowerCase().includes(s));
  });

  return (
    <div data-testid="admin-users-tab">
      <RegistrationsPanel />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h3 className="font-serif text-2xl text-navy">{tr("Utilisateurs & rôles")}</h3>
          <p className="text-sm text-slate-500 mt-1">{tr("Attribuez les rôles :")} <i>admin, président, trésorier, secrétaire, gestion, membre</i>. {tr("Le membre reçoit automatiquement un email + une notification.")}</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={tr("Rechercher (email, nom, rôle)…")}
              className="pl-9 pr-3 py-2 border border-slate-300 text-sm w-72"
              data-testid="users-search"
            />
          </div>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/treasury/members-export-full.xlsx`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-emerald-700 text-white px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-emerald-800"
            data-testid="users-export-xlsx-full"
          >
            {tr("Export XLSX complet")}
          </a>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/treasury/members-export.xlsx`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-navy text-white px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-club-red"
            data-testid="users-export-xlsx"
          >
            XLSX simple
          </a>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/treasury/members-export.csv`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-navy text-white px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-club-red"
            data-testid="users-export-csv"
          >
            CSV
          </a>
          <button
            onClick={() => setShowImport(true)}
            className="inline-flex items-center gap-2 bg-club-red text-white px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark"
            data-testid="users-import-open"
          >
            <Upload className="w-3.5 h-3.5" /> Importer (CSV / XLSX / ODS)
          </button>
        </div>
      </div>

      {status.ok && (
        <div className="bg-green-50 border border-green-300 text-green-900 p-3 text-sm mb-4 inline-flex items-center gap-2" data-testid="users-status-ok">
          <CheckCircle2 className="w-4 h-4" /> {status.ok}
        </div>
      )}
      {status.err && (
        <div className="bg-red-50 border border-red-300 text-red-800 p-3 text-sm mb-4 inline-flex items-center gap-2" data-testid="users-status-err">
          <AlertCircle className="w-4 h-4" /> {status.err}
        </div>
      )}

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-navy text-white text-left">
            <tr>
              <th className="px-4 py-3 text-xs uppercase tracking-widest font-medium">{tr("Nom")}</th>
              <th className="px-4 py-3 text-xs uppercase tracking-widest font-medium">{tr("Email")}</th>
              <th className="px-4 py-3 text-xs uppercase tracking-widest font-medium">GSM</th>
              <th className="px-4 py-3 text-xs uppercase tracking-widest font-medium">{tr("Bateau")}</th>
              <th className="px-4 py-3 text-xs uppercase tracking-widest font-medium">{tr("Statut")}</th>
              <th className="px-4 py-3 text-xs uppercase tracking-widest font-medium">{tr("Rôles")}</th>
              <th className="px-4 py-3 text-xs uppercase tracking-widest font-medium text-right">{tr("Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500" data-testid="users-empty">{tr("Aucun résultat.")}</td></tr>
            ) : filtered.map((u) => {
              const roles = u.roles && u.roles.length ? u.roles : [u.role || "member"];
              const fullName = u.name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || "—";
              return (
                <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 text-navy font-medium">{u.title ? `${u.title} ` : ""}{fullName}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{u.email}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{u.phone_gsm || u.phone || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{u.boat_name || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${u.status === "pending" ? "bg-amber-100 text-amber-800" : u.status === "rejected" ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}`}>
                      {u.status || "active"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {roles.map((r) => {
                        const meta = ROLES.find((m) => m.value === r) || ROLES[0];
                        return (
                          <span key={r} className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 ${meta.color}`}>
                            <Shield className="w-3 h-3" /> {tr(meta.label)}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => setDetailsUser(u)}
                      className="text-[10px] uppercase tracking-widest text-navy hover:underline mr-2"
                      data-testid={`details-edit-${u.id}`}
                    >{tr("Détails")}</button>
                    <button
                      onClick={() => emailFiche(u)}
                      disabled={actionBusyId === u.id}
                      className="text-[10px] uppercase tracking-widest text-emerald-700 hover:underline mr-2 disabled:opacity-60"
                      data-testid={`email-fiche-${u.id}`}
                      title={tr("Envoyer la fiche PDF par email")}
                    >
                      ✉️ {tr("Fiche")}
                    </button>
                    <button
                      onClick={() => setEditingUser(u)}
                      disabled={busyId === u.id}
                      className="text-[10px] uppercase tracking-widest text-club-red hover:underline mr-2 disabled:opacity-60"
                      data-testid={`roles-edit-${u.id}`}
                    >{tr("Rôles")}</button>
                    <button
                      onClick={() => revokeSessions(u)}
                      disabled={actionBusyId === u.id}
                      className="text-[10px] uppercase tracking-widest text-amber-700 hover:underline disabled:opacity-60"
                      data-testid={`revoke-sessions-${u.id}`}
                      title={tr("Forcer la déconnexion")}
                    >
                      {tr("Déconnecter")}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showImport && <ImportModal onClose={() => setShowImport(false)} reload={load} />}
      {detailsUser && (
        <MemberDetailsModal
          member={detailsUser}
          onClose={() => setDetailsUser(null)}
          onSaved={() => { setDetailsUser(null); load(); }}
        />
      )}
      {editingUser && (
        <RolesEditModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={async (roles) => {
            await updateRoles(editingUser.id, roles);
            setEditingUser(null);
          }}
        />
      )}
    </div>
  );
}

/* ============== Bulk import modal ============== */
function ImportModal({ onClose, reload }) {
  const [file, setFile] = useState(null);
  const [defaultRole, setDefaultRole] = useState("member");
  const [updateExisting, setUpdateExisting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setErr(tr("Sélectionnez un fichier.")); return; }
    setErr(""); setBusy(true); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("default_role", defaultRole);
    fd.append("update_existing", updateExisting ? "true" : "false");
    const ext = (file.name || "").toLowerCase();
    const isSheet = ext.endsWith(".xlsx") || ext.endsWith(".ods");
    const endpoint = isSheet ? "/admin/members/upload-spreadsheet" : "/admin/members/upload-csv";
    try {
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      reload();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Erreur d'import");
    } finally {
      setBusy(false);
    }
  };

  const copyAll = async () => {
    if (!result?.created?.length) return;
    const txt = result.created.map((u) => `${u.email};${u.password};${u.role}`).join("\n");
    try { await navigator.clipboard.writeText(txt); } catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="import-modal" onClick={onClose}>
      <div className="bg-white max-w-3xl w-full p-8 my-12 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-navy" data-testid="import-close">×</button>
        <div className="label-eyebrow text-club-red mb-2">Import massif</div>
        <h2 className="font-serif text-2xl text-navy">{tr("Importer des membres depuis un CSV")}</h2>
        <div className="divider-gold mt-3 mb-5" />

        <p className="text-sm text-slate-600 mb-3">
          Formats supportés : <b>CSV, XLSX, ODS</b>. Pour les fichiers <b>XLSX/ODS</b> (ex : votre <i>Membres 2025.ods</i>), toutes les colonnes RYCM sont reconnues automatiquement : N° FFYB, Titre, Nom, Prénom, Adresse, CP, Ville, Pays, Tel domicile, GSM, Email, Bateau, Garages, Famille, …
        </p>
        <p className="text-xs text-slate-500 mb-5">
          Les emails existants sont ignorés (sauf si <i>{tr("Mettre à jour les existants")}</i> est coché). Un mot de passe temporaire est généré pour chaque nouveau compte.
        </p>

        {!result ? (
          <form onSubmit={submit} className="space-y-4" data-testid="import-form">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 block mb-2">Fichier</label>
              <input ref={inputRef} required type="file"
                accept=".csv,.xlsx,.ods,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.oasis.opendocument.spreadsheet"
                onChange={(e) => setFile(e.target.files[0])} className="text-sm" data-testid="import-file" />
              {file && <div className="text-xs text-slate-500 mt-1">Sélectionné : {file.name} ({Math.round(file.size / 1024)} Ko)</div>}
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 block mb-2">{tr("Rôle par défaut")}</label>
              <select value={defaultRole} onChange={(e) => setDefaultRole(e.target.value)} className="border border-slate-300 px-3 py-2 text-sm" data-testid="import-default-role">
                {ROLES.map((r) => <option key={r.value} value={r.value}>{tr(r.label)}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={updateExisting} onChange={(e) => setUpdateExisting(e.target.checked)} data-testid="import-update-existing" />
              Mettre à jour les membres existants (correspondance par email)
            </label>
            {err && <div className="text-sm text-red-600" data-testid="import-err">{err}</div>}
            <button type="submit" disabled={busy} className="bg-club-red text-white px-6 py-3 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark disabled:opacity-60" data-testid="import-submit">
              {busy ? "Import en cours…" : "Lancer l'import"}
            </button>
          </form>
        ) : (
          <div data-testid="import-result">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              <Stat label={tr("Créés")}     value={result.created.length}        color="text-green-700" />
              <Stat label={tr("Mis à jour")} value={(result.updated || []).length} color="text-blue-700" />
              <Stat label={tr("Ignorés")}   value={result.skipped.length}        color="text-amber-700" />
              <Stat label="Erreurs"   value={result.errors.length}         color="text-red-700" />
            </div>

            {result.created.length > 0 && (
              <div className="bg-sand border border-slate-200 p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-widest text-slate-700">{tr("Comptes créés (mot de passe temporaire)")}</div>
                  <button onClick={copyAll} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-club-red hover:underline" data-testid="import-copy">
                    <Copy className="w-3 h-3" /> Copier
                  </button>
                </div>
                <div className="max-h-60 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-slate-500"><th className="text-left">{tr("Email")}</th><th className="text-left">{tr("Mot de passe")}</th><th className="text-left">{tr("Rôle")}</th></tr></thead>
                    <tbody>
                      {result.created.map((u, i) => (
                        <tr key={i} className="border-t border-slate-200">
                          <td className="py-1 text-navy">{u.email}</td>
                          <td className="py-1 font-mono text-club-red">{u.password}</td>
                          <td className="py-1 text-slate-600">{u.role}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {result.errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 p-4 mb-4">
                <div className="text-xs uppercase tracking-widest text-red-700 mb-2">Erreurs</div>
                <ul className="text-xs text-red-800 space-y-1 max-h-40 overflow-y-auto">
                  {result.errors.map((e, i) => (<li key={i}>Ligne {e.row} : {e.error}</li>))}
                </ul>
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={() => { setResult(null); setFile(null); if (inputRef.current) inputRef.current.value = ""; }} className="border border-navy text-navy px-5 py-2 text-xs uppercase tracking-widest hover:bg-navy hover:text-white" data-testid="import-again">
                Nouvel import
              </button>
              <button onClick={onClose} className="bg-navy text-white px-5 py-2 text-xs uppercase tracking-widest hover:bg-club-red" data-testid="import-done">
                Terminé
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="bg-white border border-slate-200 p-4 text-center">
      <div className={`font-serif text-3xl ${color || "text-navy"}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{label}</div>
    </div>
  );
}

/* ============== Roles multi-select modal ============== */
function RolesEditModal({ user, onClose, onSave }) {
  const currentRoles = user.roles && user.roles.length ? user.roles : [user.role || "member"];
  const [selected, setSelected] = useState(new Set(currentRoles));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const toggle = (r) => {
    const next = new Set(selected);
    if (next.has(r)) next.delete(r); else next.add(r);
    setSelected(next);
  };

  const submit = async () => {
    if (selected.size === 0) { setErr(tr("Sélectionnez au moins un rôle")); return; }
    setErr(""); setBusy(true);
    try { await onSave(Array.from(selected)); }
    catch (e) { setErr(e.message || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="roles-modal" onClick={onClose}>
      <div className="bg-white max-w-xl w-full p-6 md:p-8 relative my-12" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-navy" data-testid="roles-modal-close">×</button>
        <div className="label-eyebrow text-club-red mb-2">{tr("Gérer les rôles cumulés")}</div>
        <h2 className="font-serif text-2xl text-navy">{user.name || user.email}</h2>
        <p className="text-xs text-slate-500 mt-1">{user.email}</p>
        <div className="divider-gold mt-3 mb-5" />

        <p className="text-sm text-slate-600 mb-4">
          {tr("Un même utilisateur peut cumuler plusieurs rôles")} (ex : <i>{tr("Administrateur + Trésorier + Gestion")}</i>). {tr("Tous ses espaces correspondants apparaîtront automatiquement dans son Dashboard")} ▸ {tr("Mes espaces")}.
        </p>

        <div className="space-y-2 mb-5">
          {ROLES.map((r) => {
            const checked = selected.has(r.value);
            return (
              <label key={r.value} className={`flex items-center gap-3 p-3 border cursor-pointer ${checked ? "bg-sand border-club-red" : "border-slate-200 hover:border-navy"}`} data-testid={`role-checkbox-${r.value}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(r.value)}
                  className="w-4 h-4"
                />
                <span className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-widest px-2 py-1 ${r.color}`}>
                  <Shield className="w-3 h-3" /> {tr(r.label)}
                </span>
              </label>
            );
          })}
        </div>

        {err && <div className="text-sm text-red-600 mb-3" data-testid="roles-err">{err}</div>}

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 border border-navy text-navy py-3 text-xs uppercase tracking-[0.2em] hover:bg-navy hover:text-white" data-testid="roles-cancel">{tr("Annuler")}</button>
          <button onClick={submit} disabled={busy} className="flex-1 bg-club-red text-white py-3 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark disabled:opacity-60" data-testid="roles-save">
            {busy ? tr("Enregistrement…") : tr("Enregistrer")}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ============== Member details modal (RYCM extended schema) ============== */
function MemberDetailsModal({ member, onClose, onSaved }) {
  const [form, setForm] = useState({
    ffyb_number: member.ffyb_number || "", data_23: member.data_23 || "",
    title: member.title || "", first_name: member.first_name || "", last_name: member.last_name || "",
    member_type: member.member_type || "", license_type: member.license_type || "", ffyb_data: member.ffyb_data || "",
    join_date: member.join_date || "", expiration_date: member.expiration_date || "",
    address: member.address || "", postal_code: member.postal_code || "",
    city: member.city || "", country: member.country || "BELGIQUE",
    phone_home: member.phone_home || "", phone_gsm: member.phone_gsm || "",
    email: member.email || "",
    skipper_license: member.skipper_license || "", birth_date: member.birth_date || "",
    boat_name: member.boat_name || "", boat_registration: member.boat_registration || "",
    spouse_last_name: member.spouse_last_name || "", spouse_first_name: member.spouse_first_name || "",
    spouse_birth_date: member.spouse_birth_date || "",
    child1_name: member.child1_name || "", child2_name: member.child2_name || "",
    fees_notes: member.fees_notes || "", garage_code: member.garage_code || "",
    remarks: member.remarks || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  const submit = async () => {
    setBusy(true); setErr(""); setOk(false);
    try {
      await api.patch(`/admin/members/${member.id}/details`, form);
      setOk(true);
      setTimeout(onSaved, 700);
    } catch (e) {
      setErr(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const F = ({ label, name, type = "text", className = "" }) => (
    <div className={className}>
      <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">{label}</label>
      <input type={type} value={form[name] || ""} onChange={(e) => setForm({ ...form, [name]: e.target.value })}
        className="w-full border border-slate-300 px-2.5 py-1.5 text-sm" data-testid={`field-${name}`} />
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="member-details-modal" onClick={onClose}>
      <div className="bg-white max-w-4xl w-full p-6 md:p-8 relative my-6" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-navy" data-testid="member-details-close">×</button>
        <div className="label-eyebrow text-club-red mb-2">{tr("Fiche membre détaillée")}</div>
        <h2 className="font-serif text-2xl text-navy">{member.title || ""} {member.last_name || ""} {member.first_name || member.name || member.email}</h2>
        <p className="text-xs text-slate-500 mt-1">{member.email}</p>
        <div className="divider-gold mt-3 mb-5" />

        {/* Identité */}
        <Section title={tr("Identité")}>
          <F label="N° FFYB" name="ffyb_number" />
          <F label="DATA 23" name="data_23" />
          <F label={tr("Titre")} name="title" />
          <F label={tr("Nom")} name="last_name" />
          <F label={tr("Prénom")} name="first_name" />
          <F label={tr("Date de naissance")} name="birth_date" />
          <F label={tr("Type de membre")} name="member_type" />
          <F label={tr("Licence (Loisir/Compét.)")} name="license_type" />
          <F label="FFYB DATA" name="ffyb_data" />
          <F label={tr("Brevet de navigation")} name="skipper_license" />
          <F label={tr("Date d'adhésion")} name="join_date" />
          <F label={tr("Date d'expiration")} name="expiration_date" />
        </Section>

        {/* Coordonnées */}
        <Section title={tr("Coordonnées")}>
          <F label={tr("Email")} name="email" type="email" className="md:col-span-2" />
          <F label={tr("Tél. domicile")} name="phone_home" />
          <F label="GSM" name="phone_gsm" />
          <F label={tr("Adresse")} name="address" className="md:col-span-2" />
          <F label="CP" name="postal_code" />
          <F label="Ville" name="city" />
          <F label="Pays" name="country" />
        </Section>

        {/* Bateau & port */}
        <Section title="Bateau & port">
          <F label={tr("Bateau")} name="boat_name" />
          <F label="N° immatriculation" name="boat_registration" />
          <F label="Code garage" name="garage_code" />
          <F label="Cotisations / notes" name="fees_notes" className="md:col-span-3" />
        </Section>

        {/* Famille */}
        <Section title="Famille">
          <F label={tr("Nom conjoint(e)")} name="spouse_last_name" />
          <F label={tr("Prénom conjoint(e)")} name="spouse_first_name" />
          <F label={tr("Date naiss. conjoint(e)")} name="spouse_birth_date" />
          <F label={tr("Enfant 1 (nom)")} name="child1_name" />
          <F label={tr("Enfant 2 (nom)")} name="child2_name" />
        </Section>

        {/* Remarques */}
        <Section title="Remarques">
          <div className="md:col-span-3">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">{tr("Remarque générale")}</label>
            <textarea rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })}
              className="w-full border border-slate-300 px-2.5 py-1.5 text-sm" data-testid="field-remarks" />
          </div>
        </Section>

        {err && <div className="text-sm text-red-600 mt-3" data-testid="details-err">{err}</div>}
        {ok && <div className="text-sm text-green-700 mt-3" data-testid="details-ok">✓ Enregistré</div>}

        <div className="flex gap-3 mt-6 sticky bottom-0 bg-white pt-3 border-t border-slate-100">
          <button onClick={onClose} className="flex-1 border border-navy text-navy py-3 text-xs uppercase tracking-[0.2em] hover:bg-navy hover:text-white" data-testid="details-cancel">{tr("Annuler")}</button>
          <button onClick={submit} disabled={busy} className="flex-1 bg-club-red text-white py-3 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark disabled:opacity-60" data-testid="details-save">
            {busy ? tr("Enregistrement…") : tr("Enregistrer la fiche")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-5">
      <div className="text-xs uppercase tracking-[0.25em] text-club-red font-semibold mb-3 pb-1 border-b border-club-red/30">{title}</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">{children}</div>
    </div>
  );
}

